import type { Maybe } from '@adornis/base/utilTypes.js';
import { isHexColor } from '@adornis/buchungsheld-db/entities/ColorDefinitions.js';
import { css } from '@adornis/chemistry/directives/css.js';
import {
  rawToHex,
  textColorFromBackgroundColor,
  toRawColor,
} from '@adornis/chemistry/elements/theming/color-shades.js';

// Shared styling for the backend-customizable button colors (LoginColorDefinitions /
// ProfileColorDefinitions). Only plain hex colors (all the color editor produces) are
// applied; anything else is ignored and the button keeps its default appearance.

export interface ButtonColors {
  buttonColor?: Maybe<string>;
  buttonFontColor?: Maybe<string>;
}

export interface OutlineButtonColors {
  outlineButtonColor?: Maybe<string>;
}

// x-button computes its text contrast for the default accent, so a configured
// background needs an explicit font color — fall back to the same light/dark
// contrast mechanism x-button uses internally.
export function resolvedButtonFontColor(colors: Maybe<ButtonColors>): Maybe<`#${string}`> {
  const fontColor = colors?.buttonFontColor;
  if (isHexColor(fontColor)) return fontColor;
  const buttonColor = colors?.buttonColor;
  if (!isHexColor(buttonColor)) return undefined;
  return rawToHex(textColorFromBackgroundColor(toRawColor(buttonColor), toRawColor('#fff'), toRawColor('#000')));
}

// Filled buttons: background + border from buttonColor, text from buttonFontColor
// (or a readable fallback).
export function buttonColorStyle(colors: Maybe<ButtonColors>) {
  const buttonColor = colors?.buttonColor;
  const background = isHexColor(buttonColor) ? buttonColor : undefined;
  const fontColor = resolvedButtonFontColor(colors);
  return css({
    ...(background ? { background, borderColor: background } : {}),
    ...(fontColor ? { color: fontColor } : {}),
  });
}

// Outlined buttons only get border + text color so they stay outlined instead of
// becoming filled.
export function outlineButtonColorStyle(colors: Maybe<OutlineButtonColors>) {
  const outlineColor = colors?.outlineButtonColor;
  return css(isHexColor(outlineColor) ? { borderColor: outlineColor, color: outlineColor } : {});
}
